import axios from "axios";
import moment from "moment";
import { useState } from "react";
import Swal from "sweetalert2";

export const useRatePlans = ({
  rooms,
  ratePlans,
  availability,
  rentPerDayByDate,
  availableRooms,
  minimumStay,
  maximumStay,
  closeout,
  setAvailableRooms,
  setModalVisible,
  setCloseout,
  setAvailability,
  setRentPerDayByDate,
  setMinimumStay,
  setMaximumStay,
  setRatePlans,
}) => {
  const [manualRate, setManualRate] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedRatePlanId, setSelectedRatePlanId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedType, setSelectedType] = useState("");

  // Function to handle manual rate change in the modal
  const handleManualRateChange = (e) => {
    const rawValue = e.target.value.replace(/\./g, "");
    const value = parseInt(rawValue, 10);
    if (value > 0 || rawValue === "") {
      setManualRate(value);
    } else {
      Swal.fire(
        "Invalid Input",
        "Rate must be greater than 0 or Only numbers are allowed..",
        "warning"
      );
    }
  };

  const handleCellClick = (
    roomId,
    date,
    ratePlanId = null,
    type = "availability"
  ) => {
    setSelectedRoomId(roomId);
    setSelectedRatePlanId(ratePlanId);
    setSelectedType(type);
    const formattedDate = moment(date, "DD-MM-YYYY").format("DD-MM-YYYY");
    setSelectedDate(moment(formattedDate, "DD-MM-YYYY"));
    const selectedRoom = rooms.find((room) => room._id === roomId);

    // Check the type of cell clicked
    if (type === "availability") {
      setAvailableRooms(
        availability[roomId][formattedDate] || selectedRoom.maxcount
      );
      setManualRate(
        rentPerDayByDate[roomId][formattedDate] || selectedRoom.rentperday
      );
    } else if (type === "rate") {
      if (ratePlanId) {
        const ratePlan = ratePlans[roomId]?.find(
          (plan) => plan._id === ratePlanId
        );
        const ratePlanPricing = ratePlan?.rateplanpricing.find(
          (rpp) => rpp.roomId.toString() === roomId
        );

        if (ratePlanPricing) {
          if (ratePlan.ratePlanSetup === "Manual") {
            setManualRate(ratePlanPricing.manualRate[formattedDate] || "");
          } else if (ratePlan.ratePlanSetup === "Derived") {
            setManualRate(
              rentPerDayByDate[roomId][formattedDate] || selectedRoom.rentperday
            );
          }
        }
      }
      setAvailableRooms("");
    } else if (type === "minimumStay" || type === "maximumStay") {
      // Prepare minimumStay array for the selected room and date
      const minimumStayArray = Array.isArray(
        minimumStay[roomId]?.[formattedDate]
      )
        ? [...minimumStay[roomId][formattedDate]]
        : [];

      // Add ratePlanId object if it doesn't exist
      if (
        ratePlanId &&
        minimumStayArray.every((item) => item.ratePlanId !== ratePlanId)
      ) {
        minimumStayArray.push({ minStay: 0, ratePlanId });
      }

      setMinimumStay((prev) => ({
        ...prev,
        [roomId]: {
          ...prev[roomId],
          [formattedDate]: minimumStayArray,
        },
      }));

      // Prepare maximumStay array for the selected room and date
      const maximumStayArray = Array.isArray(
        maximumStay[roomId]?.[formattedDate]
      )
        ? [...maximumStay[roomId][formattedDate]]
        : [];

      // Add ratePlanId object if it doesn't exist
      if (
        ratePlanId &&
        maximumStayArray.every((item) => item.ratePlanId !== ratePlanId)
      ) {
        maximumStayArray.push({ maxStay: 0, ratePlanId });
      }

      setMaximumStay((prev) => ({
        ...prev,
        [roomId]: {
          ...prev[roomId],
          [formattedDate]: maximumStayArray,
        },
      }));
      // Handle Minimum Stay input
      setManualRate("");
      setAvailableRooms("");
    }

    // Open modal after setting the values
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    if (!selectedRoomId || !selectedDate) {
      console.error("No room or date selected");
      return;
    }

    const dateString = selectedDate.format("DD-MM-YYYY");

    try {
      const updatedAvailability = {
        ...availability[selectedRoomId],
        [dateString]:
          availableRooms || availability[selectedRoomId][dateString],
      };

      const updatedMinimumStay = {
        ...minimumStay[selectedRoomId],
        [dateString]: minimumStay[selectedRoomId][dateString] || null,
      };

      const updatedMaximumStay = {
        ...maximumStay[selectedRoomId],
        [dateString]: maximumStay[selectedRoomId]?.[dateString] || null,
      };

      if (selectedType === "rate") {
        const ratePlan = ratePlans[selectedRoomId]?.find(
          (plan) => plan._id === selectedRatePlanId
        );
        if (ratePlan) {
          const ratePlanPricing = ratePlan.rateplanpricing.find(
            (rpp) => rpp.roomId.toString() === selectedRoomId
          );

          if (ratePlan.ratePlanSetup === "Manual") {
            const manualRateUpdate = {
              [dateString]:
                manualRate || ratePlanPricing.manualRate[dateString],
            };

            await axios.put("/api/rateplans/updaterateplan", {
              ratePlanId: selectedRatePlanId,
              roomId: selectedRoomId,
              manualRate: manualRateUpdate,
            });

            setRatePlans((prevRatePlans) => {
              const updatedRatePlans = { ...prevRatePlans };
              const updatedPlan = updatedRatePlans[selectedRoomId].find(
                (plan) => plan._id === selectedRatePlanId
              );
              const updatedPricing = updatedPlan.rateplanpricing.find(
                (rpp) => rpp.roomId.toString() === selectedRoomId
              );
              updatedPricing.manualRate = {
                ...updatedPricing.manualRate,
                ...manualRateUpdate,
              };
              return updatedRatePlans;
            });
          }
        }
      } else {
        const updatedRentPerDayByDate = {
          ...rentPerDayByDate[selectedRoomId],
          [dateString]:
            manualRate || rentPerDayByDate[selectedRoomId][dateString],
        };

        await axios.put("/api/rooms/updateroomavailability", {
          roomid: selectedRoomId,
          availability: updatedAvailability,
          rentperdayByDate: updatedRentPerDayByDate,
          minimumStay: updatedMinimumStay,
          maximumStay: updatedMaximumStay,
        });

        setRentPerDayByDate((prev) => ({
          ...prev,
          [selectedRoomId]: updatedRentPerDayByDate,
        }));
      }

      setAvailability((prev) => ({
        ...prev,
        [selectedRoomId]: updatedAvailability,
      }));

      setMinimumStay((prev) => ({
        ...prev,
        [selectedRoomId]: updatedMinimumStay,
      }));

      setMaximumStay((prev) => ({
        ...prev,
        [selectedRoomId]: updatedMaximumStay,
      }));

      setModalVisible(false);
      Swal.fire(
        "Success",
        "Room availability, rates, minimum stay and closeout updated successfully!",
        "success"
      );
    } catch (err) {
      console.error("Error updating room or rate plan:", err.message);
      Swal.fire("Error", "Failed to update room details", "error");
    }
  };

  return {
    selectedType,
    selectedRatePlanId,
    selectedRoomId,
    selectedDate,
    manualRate,
    handleManualRateChange,
    handleCellClick,
    handleModalOk,
  };
};
